import type { CompiledGateEvent } from '../LevelGenerator';
import { applyGateOperation } from './gateOperations';
import { ARMY_HARD_CAP, GAMEPLAY_START } from '../../utils/constants';

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function computeBestPathMaxArmy(
  gateEvents: CompiledGateEvent[],
  startingArmy: number = GAMEPLAY_START.startingSoldiers
): number {
  let army = Math.max(0, startingArmy);
  let maxArmy = army;
  for (const event of gateEvents) {
    const [optionA, optionB] = event.options;
    const armyA = applyGateOperation(army, optionA.operation);
    const armyB = applyGateOperation(army, optionB.operation);
    army = Math.max(armyA, armyB);
    maxArmy = Math.max(maxArmy, army);
  }
  return maxArmy;
}

export function allocateByWeight(total: number, weights: number[]): number[] {
  if (weights.length === 0) return [];
  if (total <= 0) return new Array(weights.length).fill(0);

  const safeWeights = weights.map((weight) => (Number.isFinite(weight) && weight > 0 ? weight : 0));
  const weightSum = safeWeights.reduce((sum, weight) => sum + weight, 0);
  const normalizedWeights = weightSum > 0
    ? safeWeights.map((weight) => weight / weightSum)
    : new Array(weights.length).fill(1 / weights.length);

  const raw = normalizedWeights.map((weight) => total * weight);
  const base = raw.map((value) => Math.floor(value));
  let remainder = total - base.reduce((sum, value) => sum + value, 0);

  const fractionalOrder = raw
    .map((value, index) => ({
      index,
      fraction: value - Math.floor(value),
    }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);

  for (let i = 0; i < fractionalOrder.length && remainder > 0; i++) {
    base[fractionalOrder[i].index] += 1;
    remainder -= 1;
  }

  return base;
}

export function computeExpectedArmyAtProgress(
  bestPathMaxArmy: number,
  progress01: number,
  startingArmy: number = GAMEPLAY_START.startingSoldiers
): number {
  const progress = clamp(progress01, 0, 1);
  const curvedProgress = Math.pow(progress, 0.92);
  const expected = startingArmy + (bestPathMaxArmy - startingArmy) * curvedProgress * 0.85;
  return clamp(expected, startingArmy, ARMY_HARD_CAP);
}
